import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as itemActions from '@payaca/store/listedItems/listedItemsActions';

import IconButton from '@payaca/components/button/IconButton';
import ImageBlock from '@payaca/components/imageBlock/ImageBlock';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import MarkdownLabel from '@payaca/components/markdownLabel/MarkdownLabel';
import TypeToSearchField, {
  TypeToSearchRenderOptionProps,
} from '@payaca/components/typeToSearchField/TypeToSearchField';

import { JobLineItemBase } from '@payaca/types/jobContentTypes';
import {
  GetListedItemsRequestData,
  ListedItem,
  SortBy,
} from '@payaca/types/listedItemTypes';
import { SortDirection } from '@payaca/types/listViewTypes';

import { useSelector } from '@/api/state';
import { Button as AmbrosiaButton, Modal } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { clsx } from '@payaca/utilities/cvaMerge';
import './AddJobLineItemControl.sass';

interface Option {
  id: number;
  reference: string;
  description: string;
  thumbnailUrl: string;
}

type Props = {
  handleCreateJobLineItemAndOpenCreateJobLineItemModal: (
    lineItemId: number | null,
    initialItemData?: Partial<JobLineItemBase>
  ) => void;
  handleOpenAdvancedSearchLineItemModal: (searchTerm?: string) => void;
  disabledItemIds: number[];
};
const AddJobLineItemControl: FC<Props> = ({
  disabledItemIds = [],
  handleCreateJobLineItemAndOpenCreateJobLineItemModal,
  handleOpenAdvancedSearchLineItemModal,
}: Props) => {
  const dispatch = useDispatch();

  const [itemId, setItemId] = useState<number>();
  const [showRepeatedItemConfirmation, setShowRepeatedItemConfirmation] =
    useState(false);
  const [getListedItemsRequestData, setGetListedItemsRequestData] =
    useState<GetListedItemsRequestData>({
      pageSize: 50,
      pageNumber: 1,
      searchTerm: '',
      sortDirection: SortDirection.ASCENDING,
      sortBy: SortBy.NAME,
    });

  useEffect(() => {
    requestGetListedItemsPage();
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'requestGetListedItemsPage'
  }, [getListedItemsRequestData]);

  const requestGetListedItemsPage = useCallback(() => {
    dispatch(itemActions.requestGetListedItemsPage(getListedItemsRequestData));
  }, [getListedItemsRequestData, dispatch]);

  const listedItemsPage = useSelector((state: any) => {
    return state.listedItems?.listedItemsPage;
  });

  const isGettingListedItemsPage = useSelector(
    (state: any) => state.listedItems.isGettingListedItemsPage
  );

  const itemOptions = useMemo(() => {
    if (getListedItemsRequestData.searchTerm) {
      return (listedItemsPage?.items || []).map((li: ListedItem) => ({
        id: li.itemId,
        reference: li.name,
        description: li.description,
        thumbnailUrl: li.thumbnailUrl,
      }));
    }
    return [];
  }, [listedItemsPage?.items, getListedItemsRequestData.searchTerm]);

  const onSearchTermChange = (searchTerm: string) => {
    setGetListedItemsRequestData(
      (getListedItemsRequestData: GetListedItemsRequestData) => {
        return {
          ...getListedItemsRequestData,
          pageNumber: 1,
          searchTerm,
        };
      }
    );
  };

  const isOptionDisabled = useCallback(
    (optionId: number) => {
      return optionId >= 0 && disabledItemIds.includes(optionId);
    },
    [disabledItemIds]
  );

  const renderOption = (
    option: Option,
    props: TypeToSearchRenderOptionProps
  ) => {
    const isDisabled = isOptionDisabled(option.id);
    return (
      <li
        {...props}
        className={clsx(
          'listed-line-item-option flex-container flex-center',
          isDisabled && 'disabled',
          props.className
        )}
      >
        {/* create new */}
        {option.id === -1 && <IconButton size={'xs'} icon="plus.3" />}
        {/* advanced search */}
        {option.id === -2 && <UntitledIcon name="search-lg.3" />}
        {/* line item image */}
        {option.id > 0 && <ImageBlock imageSrc={option.thumbnailUrl} />}
        <div className="listed-line-item-description-reference-wrapper">
          {option.reference && (
            <span className="listed-line-item-name">{option.reference}</span>
          )}
          <MarkdownLabel
            markdown={option.description}
            className="listed-line-item-description"
          />
        </div>
      </li>
    );
  };

  return (
    <>
      <TypeToSearchField
        styleVariant={InputStyleVariant.OUTSIZE}
        options={[
          ...itemOptions,
          {
            id: -1,
            description: 'Create a new Item',
          },
          {
            id: -2,
            description: 'Advanced search',
          },
        ]}
        renderOption={renderOption}
        autoHighlight={true}
        disableFilteringInComponent={true}
        isLoadingOptions={isGettingListedItemsPage}
        onSelectOption={(option?: Option) => {
          if (option?.id === -1) {
            let initialData;
            if (getListedItemsRequestData.searchTerm) {
              initialData = {
                description: getListedItemsRequestData.searchTerm,
              };
            }
            // selected "Create a new item" - open create item modal
            handleCreateJobLineItemAndOpenCreateJobLineItemModal(
              null,
              initialData
            );
          } else if (option?.id === -2) {
            // selected "Advanced search" - open search items modal
            handleOpenAdvancedSearchLineItemModal(
              getListedItemsRequestData.searchTerm
            );
          } else if (option?.id) {
            const isDisabled = isOptionDisabled(option.id);
            if (isDisabled) {
              setItemId(option.id);
              setShowRepeatedItemConfirmation(true);
            } else {
              // selected line item - open create item modal
              handleCreateJobLineItemAndOpenCreateJobLineItemModal(option.id);
            }
          }
        }}
        placeholder="Type here to search Items"
        onSearchTermChange={onSearchTermChange}
      />
      <Modal
        isOpen={showRepeatedItemConfirmation}
        title="Item already added"
        onClose={() => {
          setItemId(undefined);
          setShowRepeatedItemConfirmation(false);
        }}
        size="sm"
      >
        <Modal.Body>Item already added, want to add again?</Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <AmbrosiaButton
              variant="outline"
              onClick={() => {
                setItemId(undefined);
                setShowRepeatedItemConfirmation(false);
              }}
            >
              No, cancel
            </AmbrosiaButton>
            <AmbrosiaButton
              onClick={() => {
                handleCreateJobLineItemAndOpenCreateJobLineItemModal(
                  itemId as number
                );
                setItemId(undefined);
                setShowRepeatedItemConfirmation(false);
              }}
            >
              Yes, add item
            </AmbrosiaButton>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddJobLineItemControl;
